/**
 * Read the CP Wealth dashboard's data out of the rendered report document.
 *
 * The renderer packs every row as gzip+base64 JSON inside the report HTML. The
 * page used to embed that HTML in an iframe; now it renders natively and only
 * wants the data, so this pulls the payload out and leaves the document's markup
 * alone.
 *
 * WHY VALUES ARE NEVER ROUNDED, HERE OR DOWNSTREAM
 * The Power BI report these numbers are checked against rounds half-UP; JS
 * toFixed rounds half-to-even. Two real cells disagree under toFixed: MRCY CV
 * (49.7249… -> 49.72 instead of 49.73) and ESLT HB (593.775 -> 593.77 instead of
 * 593.78). The server already quantises with ROUND_HALF_UP before packing, so the
 * payload literally contains 49.73 and 593.78. Format these values; never round
 * them, or the 399-cell parity check starts failing silently.
 */

export const FMT = {
  num: (v) => (v == null ? "" : v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })),
  pct: (v) => (v == null ? "" : `${(v * 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`),
  int: (v) => (v == null ? "" : String(Math.round(v))),
  txt: (v) => (v == null ? "" : String(v)),
};

export const NUMERIC = new Set(["num", "pct", "int"]);

/** Inflate a gzip+base64 payload into the shape the grid consumes. */
export async function readStockPacked(packed) {
  if (!packed) throw new Error("No dashboard data available.");
  if (typeof DecompressionStream !== "function") {
    throw new Error(
      "This browser can't decompress the report data. Please use a current " +
      "version of Chrome, Edge, Safari or Firefox."
    );
  }
  return shape(await inflate(packed));
}

/**
 * Legacy path: pull the payload out of the pre-rendered HTML report.
 *
 * Kept as a fallback so the frontend can ship before the JSON endpoint exists,
 * and so a project whose payload has not been built yet still renders.
 */
export async function readStockPayload(html) {
  const m = /^const PACKED = "([^"]*)";$/m.exec(html || "");
  if (!m) throw new Error("This report has no data payload.");
  const out = await readStockPacked(m[1]);
  // The report also carries the vintage as a plain ISO date for export filenames.
  return { ...out, isoDate: (/^const DATA_AS_OF = "([\d-]+)";/m.exec(html) || [])[1] || out.isoDate };
}

async function inflate(packed) {
  const bin = Uint8Array.from(atob(packed), (c) => c.charCodeAt(0));
  const stream = new Blob([bin]).stream().pipeThrough(new DecompressionStream("gzip"));
  return JSON.parse(await new Response(stream).text());
}

function shape(payload) {
  // Text columns are interned as indices into a shared pool; rehydrate once here
  // rather than on every render.
  const build = (cols, rows) => {
    const pooled = new Set(cols.map((c, i) => i).filter((i) => cols[i][1] === "txt" || cols[i][1] === "hid"));
    return {
      cols,
      rows: rows.map((r) => r.map((v, i) => (pooled.has(i) && v != null ? payload.pool[v] : v))),
      iIdx: cols.findIndex((c) => c[0] === "Industry"),
      sIdx: cols.findIndex((c) => c[0] === "Stock"),
      nIdx: cols.findIndex((c) => c[0] === "Notes"),
      // A hidden column stays in the row arrays so it stays filterable without
      // shifting any index; it simply never renders.
      show: cols.map((c, i) => i).filter((i) => cols[i][1] !== "hid"),
    };
  };

  const summary = build(payload.summaryCols, payload.summary);
  const detail = build(payload.detailCols, payload.detail);

  /* stock -> industry, the map that lets ONE industry control also filter the
     ranking grid, which has no industry column of its own. Built from BOTH
     tables: they never disagree on a stock's industry (checked, 0 conflicts) and
     stock_data carries 16 stocks stock_summary lacks, so using both keeps a
     security classified in only one of them reachable. */
  const indOf = new Map();
  for (const src of [detail, summary]) {
    for (const r of src.rows) {
      if (r[src.iIdx]) indOf.set(r[src.sIdx], r[src.iIdx]);
    }
  }

  // Date only, for the export filename. From the payload's own vintage, so the
  // JSON and HTML paths produce the same download name.
  const isoDate = payload.data_as_of ? String(payload.data_as_of).slice(0, 10) : null;

  return { summary, detail, industries: payload.industries, indOf, isoDate };
}
